import React, { useState, useEffect, useRef } from 'react';
import { CustomDatePicker } from './CustomDatePicker';
import { Group, Checkbox, Stack, Text, Select } from '@mantine/core';
import { Clock, Bell } from 'lucide-react';
import { getTodayISO } from '../utils/dateUtils';

interface ReminderDateTimePickerProps {
  value?: string; // ISO datetime string
  onChange: (value?: string) => void;
  defaultDate?: string; // Default date (due date if available)
  label?: string;
}

export const ReminderDateTimePicker: React.FC<ReminderDateTimePickerProps> = ({
  value,
  onChange,
  defaultDate,
  label = "Reminder"
}) => {
  const isInternalUpdate = useRef(false);
  const [enabled, setEnabled] = useState(!!value);
  const [date, setDate] = useState(() => {
    if (value) {
      return value.split('T')[0]; // Extract date part
    }
    if (defaultDate) {
      return defaultDate;
    }
    return getTodayISO();
  });
  const [time, setTime] = useState(() => {
    if (value) {
      const dateObj = new Date(value);
      return `${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
    }
    return '09:00'; // Default to 9 AM
  });

  // Generate time options (every 5 minutes from 00:00 to 23:55)
  const timeOptions = Array.from({ length: 288 }, (_, i) => {
    const hours = Math.floor(i / 12);
    const minutes = (i % 12) * 5;
    const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const label = new Date(`2000-01-01T${value}`).toLocaleTimeString([], { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return { value, label };
  });

  // Update internal state when value prop changes
  useEffect(() => {
    // Skip if this is our own internal update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (value) {
      // Parse the UTC datetime and convert to local time for display
      const valueDate = value.split('T')[0];
      const dateObj = new Date(value);
      
      // Get local time components
      const localHours = dateObj.getHours();
      const localMinutes = dateObj.getMinutes();
      const valueTime = `${String(localHours).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}`;
      
      setEnabled(true);
      setDate(valueDate);
      setTime(valueTime);
    } else {
      setEnabled(false);
      // Use utility function to avoid timezone issues
      setDate(defaultDate || getTodayISO());
      setTime('09:00');
    }
  }, [value, defaultDate]);

  // Update parent when values change
  useEffect(() => {
    if (enabled && date && time) {
      // Combine date and time into ISO string
      const combined = `${date}T${time}:00`;
      const localDate = new Date(combined);
      
      // Convert to UTC for storage
      const utcIsoString = localDate.toISOString();
      
      // Set flag before calling onChange to prevent circular updates
      isInternalUpdate.current = true;
      onChange(utcIsoString);
    } else {
      // Set flag before calling onChange to prevent circular updates
      isInternalUpdate.current = true;
      onChange(undefined);
    }
  }, [enabled, date, time, onChange]);

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange(undefined);
    }
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
  };

  const handleTimeChange = (newTime: string | null) => {
    if (newTime) {
      setTime(newTime);
    }
  };

  return (
    <Stack gap="xs">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <Checkbox
          checked={enabled}
          onChange={(event) => handleEnabledChange(event.currentTarget.checked)}
          size="sm"
          styles={{
            input: {
              width: '18px',
              height: '18px',
              minWidth: '18px',
              minHeight: '18px',
              flexShrink: 0,
            },
            body: {
              alignItems: 'center',
            },
          }}
        />
        <Group gap={6} align="center" style={{ flex: 1, minHeight: '18px' }}>
          <Bell size={14} style={{ color: 'var(--app-text-primary)' }} />
          <Text size="sm" fw={500} c="var(--app-text-primary)">{label}</Text>
        </Group>
      </div>
      
      {enabled && (
        <Stack gap="sm" pl="md">
          <Stack gap="sm">
            <div>
              <Text size="xs" c="dimmed" mb={4}>Date</Text>
              <CustomDatePicker
                value={date}
                onChange={handleDateChange}
                placeholder="Select date"
              />
            </div>
            
            <div>
              <Text size="xs" c="dimmed" mb={4}>Time</Text>
              <Select
                data={timeOptions}
                value={time}
                onChange={handleTimeChange}
                placeholder="Select time"
                searchable
                size="sm"
                leftSection={<Clock size={14} />}
                comboboxProps={{
                  withinPortal: true,
                  position: 'bottom-start',
                  middlewares: { flip: true, shift: true },
                  offset: 4
                }}
              />
            </div>
          </Stack>
          
          {value && (
            <Text size="xs" c="dimmed">
              Reminder will be sent on {new Date(value).toLocaleDateString()} at {new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};